import { Activity, Info } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDistrict } from "../utils/DistrictContext";
import { useTheme } from "../context/ThemeContext";
import { createPortal } from "react-dom";
import InfoTooltip from "./ui/InfoTooltip";
import { Spin } from "antd";


export function FlowHealthWidget({ data, loading }) {
  const navigate = useNavigate();
  const { theme, colors } = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [totalDevices, setTotalDevices] = useState(0);
  const [lastScan, setLastScan] = useState("");
  const selectedDistrict = useDistrict();
  const [showBackflowInfo, setShowBackflowInfo] = useState(false);

  useEffect(() => {
    if (!data) return;

   const mapped = [
  {
    label: "Backflow",
    key: "backflow",
    count: data.backflow,
    percentage: Math.round(data.backflow_per),
    color: "#ff6b6b",        // RED
    lightColor: "#ff6b6b",
    info: "A condition in which reverse water flow..."
  },
  {
    label: "Leak Detected",
    key: "leak",
    count: data.leak,
    percentage: Math.round(data.leak_per),
    color: "#feca57",        // YELLOW
    lightColor: "#feca57",
  },
  {
    label: "No Recent Flow",
    key: "no_recent_flow",
    count: data.no_recent_flow,
    percentage: Math.round(data.no_recent_per),
    color: "#1dd1a1",        // PURPLE
    lightColor: "#1dd1a1",
  },
  {
    label: "No Issue",
    key: "no_issue",
    count: data.no_issue,
    percentage: Math.round(data.no_issue_per),
    color: "#48dbfb",        // GREEN
    lightColor: "#48dbfb",
  },
]

    setBreakdown(mapped);
    setTotalDevices(
      data.no_recent_flow + data.leak + data.backflow + data.no_issue,
    );
    setLastScan(new Date().toLocaleString());
  }, [data]);

  const handleNavigate = (tabKey) => {
    console.log("CLICKED PIE:", tabKey);
    navigate("/flow-health", { state: { tab: tabKey } });
  };

  // const calculatePieSegments = () => {
  //   let cumulative = 0;
  //   return breakdown.map((item) => {
  //     const startAngle = (cumulative / 100) * 360;
  //     cumulative += item.percentage;
  //     const endAngle = (cumulative / 100) * 360;
  //     return { ...item, startAngle, endAngle };
  //   });
  // };

  const calculatePieSegments = () => {
  const total = breakdown.reduce((sum, item) => sum + item.percentage, 0);

  let cumulative = 0;

  return breakdown.map((item) => {
    const normalized = (item.percentage / total) * 100;

    const startAngle = (cumulative / 100) * 360;
    cumulative += normalized;
    const endAngle = (cumulative / 100) * 360;

    return {
      ...item,
      normalizedPercentage: normalized,
      startAngle,
      endAngle,
    };
  });
};
  const segments = calculatePieSegments();

  const createPieSlice = (startAngle, endAngle, radius) => {
    const startRadians = (startAngle - 90) * (Math.PI / 180);
    const endRadians = (endAngle - 90) * (Math.PI / 180);

    const x1 = 120 + radius * Math.cos(startRadians);
    const y1 = 120 + radius * Math.sin(startRadians);
    const x2 = 120 + radius * Math.cos(endRadians);
    const y2 = 120 + radius * Math.sin(endRadians);

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return `M 120 120 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };

  const MetricInfo = ({ text }) => {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useState(null)[0];

    const handleToggle = (e) => {
      e.stopPropagation();

      const rect = e.currentTarget.getBoundingClientRect();

      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX - 80,
      });

      setShow(!show);
    };

    return (
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <InfoTooltip text="A condition in which reverse water flow of more than 1 gallon has been reported in the past 30 days. Clears when no backflow is detected for 30 consecutive days."></InfoTooltip>

        {/* <button
          ref={buttonRef}
          onClick={handleToggle}
          className="flex items-center justify-center w-5 h-5 rounded-full ml-1 transition-all hover:scale-110"
          style={{
            backgroundColor: show
              ? `${colors.primary}25`
              : `${colors.primary}15`,
            color: colors.primary,
          }}
        >
          
          <Info className="w-3 h-3" />
        </button> */}

        {show &&
          createPortal(
            <>
              <div
                className="fixed inset-0 z-9998"
                onClick={() => setShow(false)}
              />

              {/* Popup  */}
              <motion.div
                className="fixed rounded-lg shadow-xl border z-9999"
                style={{
                  backgroundColor:
                    theme === "white" ? "#ffffff" : colors.background,
                  borderColor: `${colors.primary}30`,
                  boxShadow: `0 8px 32px ${colors.primary}30`,
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                  width: "200px",
                }}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <div className="px-4 py-3">
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: colors.textSecondary }}
                  >
                    {text}
                  </p>
                </div>

                {/* Arrow */}
                <div
                  className="absolute -top-1.5 left-6 w-3 h-3 rotate-45 border-t border-l"
                  style={{
                    backgroundColor:
                      theme === "white" ? "#ffffff" : colors.background,
                    borderColor: `${colors.primary}30`,
                  }}
                />
              </motion.div>
            </>,
            document.body,
          )}
      </div>
    );
  };

  return (
    <motion.div
      className="rounded-xl p-6 transition-colors duration-300 relative overflow-hidden h-full flex flex-col group"
      style={{
        backgroundColor: theme === "white" ? "#ffffff" : colors.glassEffect,
        // backgroundColor: colors.cardBg,
        backdropFilter: "blur(10px)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: theme === "white" ? colors.border : `${colors.primary}15`,
      }}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7, delay: 0.15, type: "spring", bounce: 0.4 }}
      whileHover={{
        x: 10,
        transition: { type: "spring", stiffness: 300 },
      }}
    >
      <div
        className="absolute top-0 left-0 h-0.5
               w-0 group-hover:w-full transition-all duration-400 ease-out rounded-full"
        style={{ backgroundImage: colors.gradient }}
      />

      <div className="relative z-10">
        {loading || !data ? (
          <div className="flex items-center justify-center h-[300px]">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundImage: colors.gradient }}
              >
                <Activity className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-2xl font-bold" style={{ color: colors.text }}>
                Flow Health
              </h3>
            </div>

            <div className="flex flex-col items-center mb-5">
              <svg width="240" height="240" viewBox="0 0 240 240">
                <defs>
                  {segments.map((item, index) => (
                    <linearGradient
                      key={index}
                      id={`pieGradient-${index}`}
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor={item.color} />
                      <stop offset="100%" stopColor={item.lightColor} />
                    </linearGradient>
                  ))}
                </defs>

                {segments.map((item, index) => {
                  const path = createPieSlice(item.startAngle, item.endAngle, 100);

                  const midAngle = (item.startAngle + item.endAngle) / 2;
                  const midRadians = (midAngle - 90) * (Math.PI / 180);
                  const offsetX = Math.cos(midRadians) * 10;
                  const offsetY = Math.sin(midRadians) * 10;
                  const isHovered = hoveredIndex === index;

                  return (
                    <g key={index}>
                      <motion.path
                        d={path}
                        fill={`url(#pieGradient-${index})`}
                        stroke={colors.border}
                        strokeWidth="3"
                        animate={{
                          opacity: isHovered ? 1 : hoveredIndex !== null ? 0.5 : 1,
                          x: isHovered ? offsetX : 0,
                          y: isHovered ? offsetY : 0,
                        }}
                        whileHover={{ scale: 1.1, cursor: "pointer" }}
                        style={{ transformOrigin: "120px 120px" }}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        onClick={() => handleNavigate(item.key)}
                      />

                      {isHovered && (
                        <motion.g
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <motion.text
                            x={120 + Math.cos(midRadians) * 70}
                            y={120 + Math.sin(midRadians) * 70}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className="text-lg font-bold"
                            fill={colors.text}
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          >
                            {item.percentage}%
                          </motion.text>
                        </motion.g>
                      )}
                    </g>
                  );
                })}

                <circle cx="120" cy="120" r="35" fill={colors.background} />
              </svg>

              <div className="w-full grid grid-cols-2 max-[1650px]:grid-cols-1 gap-3 mt-6">
                {breakdown.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleNavigate(item.key)}
                    className="flex items-center border gap-3 p-3 rounded-lg transition-colors cursor-pointer"
                    style={{
                      backgroundColor: `${colors.primary}10`,
                      borderColor: `${colors.primary}20`,
                    }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <div
                      className="w-4 h-4 rounded"
                      style={{
                        background: `linear-gradient(135deg, ${item.color}, ${item.lightColor})`,
                      }}
                    />
                    <div>
                      <div
                        className="flex items-center gap-1 text-sm"
                        style={{ color: colors.text }}
                      >
                        {item.label}

                        {item.key === "backflow" && <MetricInfo text={item.info} />}
                      </div>
                      <div
                        className="text-xs"
                        style={{ color: colors.textSecondary }}
                      >
                        ({item.count}) — {item.percentage}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="pt-4 border-t flex justify-between text-sm"
              style={{ borderColor: colors.border }}
            >
              <span
                className="font-semibold"
                style={{ color: colors.success || colors.primary }}
              >
                Total Devices: {totalDevices}
              </span>
              {/* <span className="text-gray-400">
                Last Scan:{" "}
                {new Date(lastScan).toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                })}
              </span> */}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
